import { Types as SocketTypes } from '../../../actions/socket'
import { Socket } from '../../../services'
import { EstateUtils } from '../../../utils'
import * as SocketPrivateActions from './socket.actions'

function getSessionId(state) {
    const {
        user: {
            session: { sessionId },
        },
    } = state
    return sessionId
}

/**
 * Socket middleware
 * @param {Object} store
 */
const socketMiddleware = (store) => {
    const { dispatch, getState } = store

    // Socket callbacks
    const callbacks = {
        [Socket.CallbacksKeys.CONNECT]: () => dispatch(SocketPrivateActions.onConnecting()),
        [Socket.CallbacksKeys.CONNECTED]: () => dispatch(SocketPrivateActions.onConnection()),
        [Socket.CallbacksKeys.DEVICE_CONNECTION]: (...p) => dispatch(SocketPrivateActions.onDeviceConnection(...p)),
        [Socket.CallbacksKeys.DEVICE_DISCONNECTION]: (...p) => dispatch(SocketPrivateActions.onDeviceDisconnection(...p)),
        [Socket.CallbacksKeys.DEVICE_DOWNLOAD_PERCENTAGE]: (...p) => dispatch(SocketPrivateActions.onDownloadPercentage(...p)),
        [Socket.CallbacksKeys.DEVICE_FEEDBACK]: (...p) => dispatch(SocketPrivateActions.onDeviceFeedback(...p)),
        [Socket.CallbacksKeys.DEVICE_FETCH_CONNECTIONS]: (...p) => dispatch(SocketPrivateActions.onFetchConnectedDevices(...p)),
        [Socket.CallbacksKeys.DEVICE_SCREENSHOT]: (...p) => dispatch(SocketPrivateActions.onScreenshot(...p)),
        [Socket.CallbacksKeys.DISCONNECT]: () => dispatch(SocketPrivateActions.onDisconnecting()),
        [Socket.CallbacksKeys.DISCONNECTED]: () => dispatch(SocketPrivateActions.onDisconnection()),
        [Socket.CallbacksKeys.DEVICE_OPEN_STATUS_PANEL]: (...p) => dispatch(SocketPrivateActions.onOpenStatusPanel(...p)),
        [Socket.CallbacksKeys.DEVICE_CLEAR_CACHE]: (...p) => dispatch(SocketPrivateActions.onClearDeviceCache(...p)),
        [Socket.CallbacksKeys.DEVICE_CLEAR_CONTENT]: (...p) => dispatch(SocketPrivateActions.onClearDeviceContent(...p)),
    }

    // Socket instance
    let socket

    // Return the handler that will be called for each action dispatched
    return (next) =>
        async (action = {}) => {
            try {
                const { type, payload } = action

                switch (type) {
                    // CONNECT
                    case SocketTypes.SOCKET_CONNECT: {
                        const sessionId = getSessionId(getState())
                        const { url } = payload

                        try {
                            if (socket) {
                                socket.close()
                            }

                            socket = new Socket(callbacks)

                            const socketUrl = url || EstateUtils.getSocketServer(getState())

                            // Perform the connection
                            socket.connect(socketUrl, `hubble${sessionId}`)
                        } catch (error) {
                            console.error('Cannot find socket server for current estate')
                        }
                        break
                    }
                    // CONNECT BLOCKING
                    case SocketTypes.SOCKET_CONNECT_BLOCKING: {
                        const sessionId = getSessionId(getState())
                        const { url } = payload
                        await new Promise((resolve, reject) => {
                            try {
                                if (socket) {
                                    socket.close()
                                }

                                const callbacksOverloaded = {
                                    ...callbacks,
                                    [Socket.CallbacksKeys.CONNECTED]: () => {
                                        callbacks[Socket.CallbacksKeys.CONNECTED]()
                                        resolve()
                                    },
                                }
                                socket = new Socket(callbacksOverloaded)

                                let socketUrl = url || EstateUtils.getSocketServer(getState())

                                // Perform the connection
                                socket.connect(socketUrl, sessionId)
                            } catch (error) {
                                reject(new Error('Cannot find socket server for current estate'))
                            }

                            next(action)
                        })
                        break
                    }
                    // DISCONNECT
                    case SocketTypes.SOCKET_DISCONNECT: {
                        if (!socket) {
                            return null
                        }

                        socket.close()
                        break
                    }
                    // FETCH CONNECTED DEVICES
                    case SocketTypes.SOCKET_FETCH_CONNECTED_DEVICES: {
                        if (!socket) {
                            return null
                        }

                        socket.fetchConnectedDevices()
                        break
                    }
                    // REBOOT DEVICE
                    case SocketTypes.SOCKET_REBOOT_DEVICE: {
                        if (!socket) {
                            return null
                        }

                        socket.rebootDevice(payload.uid, payload.groupId)
                        break
                    }
                    // UPDATE CONTENT
                    case SocketTypes.SOCKET_UPDATE_CONTENT: {
                        if (!socket) {
                            return null
                        }

                        socket.updateContent(payload.uid, payload.groupId)
                        break
                    }
                    // UPDATE SETTINGS
                    case SocketTypes.SOCKET_UPDATE_SETTINGS: {
                        if (!socket) {
                            return null
                        }

                        socket.updateSettings(payload.uid, payload.groupId)
                        break
                    }
                    // DEMAND SCREENSHOT
                    case SocketTypes.SOCKET_DEMAND_SCREENSHOT: {
                        if (!socket) {
                            return null
                        }

                        socket.demandScreenshot(payload.uid, payload.groupId)
                        break
                    }
                    // OPEN STATUS PANEL
                    case SocketTypes.SOCKET_OPEN_STATUS_PANEL: {
                        if (!socket) {
                            return null
                        }

                        socket.openStatusPanel(payload.uid)
                        break
                    }
                    case SocketTypes.SOCKET_CLEAR_DEVICE_CACHE: {
                        if (!socket) {
                            return null
                        }

                        socket.clearDeviceCache(payload.uid)
                        break
                    }
                    case SocketTypes.SOCKET_CLEAR_DEVICE_CONTENT: {
                        if (!socket) {
                            return null
                        }

                        socket.clearDeviceContent(payload.uid)
                        break
                    }
                    case SocketTypes.SOCKET_RESTART_APP: {
                        if (!socket) {
                            return null
                        }

                        socket.restartApp(payload.uid)
                        break
                    }
                    case SocketTypes.SOCKET_RELOAD_PROMO: {
                        if (!socket) {
                            return null
                        }

                        socket.reloadPromo(payload.uid)
                        break
                    }
                    default:
                        break
                }
            } catch (error) {
                console.error(error)
            }

            return next(action)
        }
}

export default socketMiddleware
